'use client'

import { useCallback, useState, useRef, useMemo, useEffect, useLayoutEffect } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { MentionNode } from './MentionNode'
import SlashCommandPlugin from './SlashCommandPlugin'
import CharacterMentionPlugin from './CharacterMentionPlugin'
import SerializationPlugin from './SerializationPlugin'
import InitializationPlugin from './InitializationPlugin'
import MentionDeletionPlugin from './MentionDeletionPlugin'
import TextStylingPlugin from './TextStylingPlugin'
import CharacterLineEnforcerPlugin from './CharacterLineEnforcerPlugin'
import SelectionPlugin, { clearHighlight } from './SelectionPlugin'
import CursorContextPlugin from './CursorContextPlugin'
import PersistentCaretPlugin from './PersistentCaretPlugin'
import RestoreCaretPlugin from './RestoreCaretPlugin'
import ToolbarPlugin from './ToolbarPlugin'
import { extractSegments, spliceSegment } from './scriptUtils'
import { FaArrowUp } from 'react-icons/fa'
import { FiX, FiUser } from 'react-icons/fi'
import { LuLoaderCircle, LuTextSelect } from 'react-icons/lu'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import { useEpisode } from '@/contexts/EpisodeContext'
import { useShow } from '@/contexts/ShowContext'
import Loader from '@/components/loader'

const EDITOR_THEME = {
    paragraph: 'editor-paragraph',
    text: {
        bold: 'editor-text-bold',
        italic: 'editor-text-italic',
        underline: 'editor-text-underline',
    },
}

const DEFAULT_COMMANDS = [
    { id: 'segment', label: 'Segment', description: 'Define the beginning of a new segment' },
    { id: 'scene', label: 'Scene', description: 'Define the beginning of a new scene' },
    // { id: 'description', label: 'Description', description: 'Add a description for other services' },
    { id: 'break', label: 'Break', description: 'Add a break in the speech', data: { duration: 1 } },
    { id: 'emotion', label: 'Emotion', description: 'Set the character emotion', data: { emotion: '' } },
]

const DEFAULT_SETTINGS = {
    showHeader: true,
    showBackButton: true,
    showCrumbs: true,
    showSegments: true,
    segment: null,
    commands: null,
    allowCharacter: true,
    character: null,
}

export default function RichTextEditor({
    initialValue = '',
    onChange,
    characters = [],
    commands = DEFAULT_COMMANDS,
    placeholder = 'Start typing... Use / for commands, # for characters',
    className = '',
    settings,
    showAssetsToggle = false,
    assetsVisible = false,
    onToggleAssets,
}) {
    const mergedSettings = useMemo(
        () => ({ ...DEFAULT_SETTINGS, ...(settings || {}) }),
        [settings]
    )
    const initialConfig = {
        namespace: 'RichTextEditor',
        theme: EDITOR_THEME,
        nodes: [MentionNode],
        onError: (error) => console.error('Lexical error:', error),
    }

    const { show } = useShow()
    const { episode, fetchEpisode } = useEpisode()
    const { runTaskAndPoll, taskStatus } = useKagamiBridge()

    const fullScriptRef = useRef(initialValue)
    const [activeSegmentId, setActiveSegmentId] = useState(mergedSettings.segment || null)
    const [editorKey, setEditorKey] = useState(0)
    const [scriptRevision, setScriptRevision] = useState(0)
    const [prompt, setPrompt] = useState('')
    const [working, setWorking] = useState(false)
    const [selectedText, setSelectedText] = useState(null)
    const [editorContext, setEditorContext] = useState({
        segment: null,
        scene: null,
        description: null,
        character: null,
        mode: null,
    })
    const caretIndexRef = useRef(null)
    const promptRef = useRef(null)
    const scrollContainerRef = useRef(null)
    const pendingScrollTopRef = useRef(0)
    const pendingCaretIndexRef = useRef(null)

    // Keep the editor in sync when the script is updated externally (e.g. LLM
    // edits applied via the scripting agent). When the incoming initialValue
    // differs from our local ref, snapshot the user's scroll/caret position,
    // refresh the ref and remount Lexical so the InitializationPlugin
    // re-seeds the editor with the new content. The captured snapshots are
    // restored after remount in useLayoutEffect / RestoreCaretPlugin below.
    useEffect(() => {
        if (initialValue !== fullScriptRef.current) {
            pendingScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? 0
            pendingCaretIndexRef.current = caretIndexRef.current

            fullScriptRef.current = initialValue
            setActiveSegmentId(null)
            setEditorKey(k => k + 1)
            setScriptRevision(r => r + 1)
        }
    }, [initialValue])

    // Restore scroll position immediately after the editor remounts so the
    // user doesn't see the script jump back to the top.
    useLayoutEffect(() => {
        const target = pendingScrollTopRef.current
        if (!target) return
        const el = scrollContainerRef.current
        if (!el) return
        el.scrollTop = target
        // Re-apply on the next frame too — InitializationPlugin populates
        // the editor via editor.update() asynchronously, which can grow the
        // scroll height and clamp our initial assignment.
        const raf = requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = target
            }
        })
        return () => cancelAnimationFrame(raf)
    }, [editorKey])

    const segments = useMemo(() => extractSegments(fullScriptRef.current), [editorKey, scriptRevision])

    const activeSegment = activeSegmentId
        ? segments.find(s => s.id === activeSegmentId)
        : null

    const visibleValue = activeSegment
        ? fullScriptRef.current.slice(activeSegment.start, activeSegment.end)
        : fullScriptRef.current

    const handleChange = useCallback((value) => {
        if (!onChange) return

        if (activeSegment) {
            const newFull = spliceSegment(
                fullScriptRef.current,
                activeSegment.start,
                activeSegment.end,
                value
            )
            fullScriptRef.current = newFull
            const delta = value.length - (activeSegment.end - activeSegment.start)
            activeSegment.end = activeSegment.start + value.length
            for (const seg of segments) {
                if (seg.start > activeSegment.start) {
                    seg.start += delta
                    seg.end += delta
                }
            }
            onChange(newFull)
        } else {
            fullScriptRef.current = value
            onChange(value)
        }
        setScriptRevision(r => r + 1)
    }, [onChange, activeSegment, segments])

    const lockedSegment = !!mergedSettings.segment

    const handleSegmentChange = useCallback((segmentId) => {
        if (lockedSegment) return
        setActiveSegmentId(segmentId)
        setEditorKey(k => k + 1)
    }, [lockedSegment])

    useEffect(() => {
        const target = mergedSettings.segment || null
        if (target !== activeSegmentId) {
            setActiveSegmentId(target)
            setEditorKey(k => k + 1)
        }
    }, [mergedSettings.segment])

    const effectiveCommands = useMemo(() => {
        let result = commands
        if (mergedSettings.character) {
            result = result.filter(c => c.id === 'break' || c.id === 'emotion')
        } else if (Array.isArray(mergedSettings.commands)) {
            const allowed = new Set(mergedSettings.commands)
            result = result.filter(c => allowed.has(c.id))
        }
        return result
    }, [commands, mergedSettings.character, mergedSettings.commands])

    const lockedCharacter = mergedSettings.character
        ? characters.find(c => (c._id || c.id) === mergedSettings.character)
        : null

    // When the caret is inside a character's speech block, resolve a display
    // name for the indicator. The mention itself stores an empty name, so look
    // it up from the characters list, falling back to the show or context.
    const activeSpeechCharacter = useMemo(() => {
        if (editorContext?.mode !== 'character' || !editorContext.character) return null
        const id = editorContext.character.id
        const found = characters.find(c => (c._id || c.id) === id)
            || show?.characters?.find(c => (c._id || c.id) === id)
        return {
            id,
            name: found?.name || editorContext.character.name || 'Character',
        }
    }, [editorContext, characters, show?.characters])

    const headerTitle = lockedCharacter?.name ?? episode?.name ?? 'Episode'

    const showSegmentDropdown = mergedSettings.showSegments && !lockedSegment && !lockedCharacter

    const allowCharacterMentions = mergedSettings.allowCharacter && !lockedCharacter

    const slashForcedMode = lockedCharacter ? 'character' : null

    const handlePromptSubmit = useCallback(async (overrideMessage) => {
        const isOverride = typeof overrideMessage === 'string'
        const candidate = isOverride ? overrideMessage : prompt
        if (!candidate?.trim() || working) return
        const message = candidate.trim()
        const selection = selectedText
        const ctx = editorContext
        if (!isOverride) {
            setPrompt('')
            if (promptRef.current) {
                promptRef.current.style.height = 'auto'
            }
        }
        setSelectedText(null)

        // If the user hasn't placed a caret yet but the script has segments,
        // fall back to the first real segment. A caret placed outside any
        // segment marker (e.g. preamble) is intentionally left unresolved.
        const realSegments = segments.filter(s => s.id !== '__preamble__')
        const noCaretYet = caretIndexRef.current == null
        const segmentFallback = noCaretYet && !ctx.segment && realSegments.length > 0
            ? { id: realSegments[0].id, name: realSegments[0].label }
            : null

        const fullEditorContext = {
            ...ctx,
            segment: ctx.segment ?? segmentFallback,
            viewingSegmentId: activeSegment?.id ?? null,
        }

        try {
            setWorking(true)
            await runTaskAndPoll({
                function: 'scripting-agent',
                showId: show._id,
                payload: {
                    episodeId: episode._id,
                    prompt: message,
                    ...(selection ? { selectedText: selection } : {}),
                    ...(caretIndexRef.current != null ? { caretIndex: caretIndexRef.current } : {}),
                    editorContext: fullEditorContext,
                    mode: isOverride ? 'inline' : null,
                },
            })
            await fetchEpisode()
        } catch (error) {
            console.error('Scripting agent error:', error)
        } finally {
            setWorking(false)
        }
    }, [prompt, working, selectedText, editorContext, activeSegment, segments, runTaskAndPoll, show?._id, episode?._id])

    const handlePromptKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            handlePromptSubmit()
        }
    }, [handlePromptSubmit])

    return (
        <div className={`rich-text-editor relative flex flex-col w-full h-full ${className}`}>
            <LexicalComposer key={editorKey} initialConfig={initialConfig}>
                <div className="flex flex-col w-full h-full">
                    <ToolbarPlugin
                        segments={segments}
                        activeSegmentId={activeSegmentId}
                        onSegmentChange={handleSegmentChange}
                        episode={episode}
                        editorContext={editorContext}
                        showHeader={mergedSettings.showHeader}
                        showBackButton={mergedSettings.showBackButton}
                        showCrumbs={mergedSettings.showCrumbs}
                        showSegments={showSegmentDropdown}
                        title={headerTitle}
                        showAssetsToggle={showAssetsToggle}
                        assetsVisible={assetsVisible}
                        onToggleAssets={onToggleAssets}
                    />
                    <div
                        ref={scrollContainerRef}
                        className={`flex flex-col flex-1 w-full h-full items-center overflow-y-auto p-4 pb-48 ${working ? 'pointer-events-none blur-sm' : ''}`}
                    >
                        <div className="relative w-full max-w-3xl">
                            <RichTextPlugin
                                contentEditable={
                                    <ContentEditable
                                        className="w-full h-full min-h-[120px] px-7 py-3 font-mono text-[14px] text-secondary-foreground/70 bg-transparent outline-none resize-none leading-[1.5] [&>p]:my-0.5 [&>p]:min-h-[1lh]"
                                    />
                                }
                                placeholder={
                                    <div className="absolute top-3 left-4 text-sm text-gray-500 pointer-events-none select-none">
                                        {placeholder}
                                    </div>
                                }
                                ErrorBoundary={LexicalErrorBoundary}
                            />
                            <HistoryPlugin />
                            <InitializationPlugin initialValue={visibleValue} />
                            <SerializationPlugin onChange={handleChange} />
                            <SlashCommandPlugin commands={effectiveCommands} forcedMode={slashForcedMode} />
                            {allowCharacterMentions && (
                                <CharacterMentionPlugin characters={characters} />
                            )}
                            <MentionDeletionPlugin />
                            <TextStylingPlugin />
                            <CharacterLineEnforcerPlugin />
                            <SelectionPlugin onSelectionChange={setSelectedText} />
                            <CursorContextPlugin onContextChange={setEditorContext} onCaretIndexChange={v => { caretIndexRef.current = v }} />
                            <PersistentCaretPlugin />
                            <RestoreCaretPlugin index={pendingCaretIndexRef.current} />
                        </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 z-50 flex items-center justify-center px-10 pointer-events-none pb-4 bg-background">
                        <div className="max-w-3xl w-full flex flex-col pointer-events-auto">
                            <div className={`flex flex-col p-3 bg-card border border-secondary gap-3 w-full rounded-xl shadow-lg ${working ? 'pointer-events-none' : ''}`}>
                                <textarea
                                    ref={promptRef}
                                    value={prompt}
                                    onChange={e => {
                                        setPrompt(e.target.value)
                                        if (e.target) {
                                            e.target.style.height = 'auto'
                                            e.target.style.height = `${e.target.scrollHeight}px`
                                        }
                                    }}
                                    onKeyDown={handlePromptKeyDown}
                                    className="w-full font-mono placeholder:text-secondary-foreground/30 text-secondary-foreground p-0 max-h-60 bg-transparent border-none outline-hidden text-xs/4 resize-none"
                                    rows={1}
                                    placeholder="Ask AI to help you write..."
                                    disabled={working}
                                />
                                <div className="flex items-center gap-2">
                                    <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                                        {activeSpeechCharacter && (
                                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-500/10 border border-indigo-500/30 rounded-md text-xs text-indigo-300 max-w-full">
                                                <FiUser className="w-3 h-3 shrink-0 opacity-70" />
                                                <span className="truncate max-w-48">
                                                    Speech: <span className="font-medium">{activeSpeechCharacter.name}</span>
                                                </span>
                                            </div>
                                        )}
                                        {selectedText && (
                                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/20 rounded-md text-xs text-primary max-w-full">
                                                <LuTextSelect className="w-3 h-3 shrink-0 opacity-70" />
                                                <span className="truncate opacity-70 max-w-60">{selectedText}</span>
                                                <button
                                                    onClick={() => { clearHighlight(); setSelectedText(null) }}
                                                    className="shrink-0 ml-auto opacity-50 hover:opacity-100"
                                                    title="Clear selection"
                                                >
                                                    <FiX className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0 ml-auto">
                                        {working && taskStatus && (
                                            <span className="flex items-center gap-2 text-xs text-secondary-foreground/50">
                                                <LuLoaderCircle className="w-3 h-3 animate-spin" />
                                                {taskStatus}
                                            </span>
                                        )}
                                        <button
                                            onClick={() => handlePromptSubmit()}
                                            disabled={working || !prompt.trim()}
                                            className="flex items-center justify-center w-6 h-6 rounded-md bg-primary text-primary-foreground hover:bg-primary/80 transition-colors disabled:opacity-40"
                                        >
                                            {working
                                                ? <LuLoaderCircle className="w-3 h-3 animate-spin" />
                                                : <FaArrowUp className="w-3 h-3" />
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </LexicalComposer>
        </div>
    )
}
